import React, { useEffect, useRef } from 'react';

const MobileHistorySheet = ({ 
  isOpen, 
  onClose, 
  conversations, 
  onSelectConversation,
  onNewConversation 
}) => {
  const sheetRef = useRef(null);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Get last 5 sessions
  const recentSessions = conversations.slice(0, 5);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`mobile-sheet-overlay ${isOpen ? 'visible' : ''}`}
        onClick={handleOverlayClick}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'all' : 'none',
          transition: 'opacity 0.3s ease',
          zIndex: 200
        }}
      />
      
      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="mobile-history-sheet"
        data-testid="mobile-history-sheet"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#0a0809',
          borderTop: '1px solid rgba(255, 243, 225, 0.08)',
          borderRadius: '20px 20px 0 0',
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          zIndex: 201,
          maxHeight: '70vh',
          overflowY: 'auto'
        }}
      >
        {/* Drag handle */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '12px 0 8px 0'
          }}
        >
          <div
            style={{
              width: '40px',
              height: '4px',
              borderRadius: '2px',
              background: 'rgba(255, 255, 255, 0.2)'
            }}
          />
        </div>
        
        {/* Header */}
        <div 
          style={{
            padding: '8px 24px 16px 24px',
            borderBottom: '1px solid rgba(255, 243, 225, 0.06)'
          }}
        >
          <h3
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '18px',
              fontWeight: 300,
              fontStyle: 'italic',
              color: 'rgba(255, 243, 225, 0.6)',
              margin: 0
            }}
          >
            recent sessions
          </h3>
        </div>
        
        {/* Sessions list */}
        <div style={{ padding: '8px 0' }}>
          {recentSessions.length === 0 ? (
            <div 
              style={{
                padding: '32px 24px',
                textAlign: 'center'
              }}
            >
              <p
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: '16px',
                  fontStyle: 'italic',
                  color: 'rgba(255, 243, 225, 0.3)'
                }}
              >
                No sessions yet
              </p>
            </div>
          ) : (
            recentSessions.map((session, index) => (
              <button
                key={session.id}
                onClick={() => {
                  onSelectConversation(session.id);
                  onClose();
                }}
                data-testid={`mobile-session-${session.id}`}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: index < recentSessions.length - 1 
                    ? '1px solid rgba(255, 243, 225, 0.04)' 
                    : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  transition: 'background 0.2s ease'
                }}
                onTouchStart={(e) => e.currentTarget.style.background = 'rgba(255, 243, 225, 0.04)'}
                onTouchEnd={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: '17px',
                    fontWeight: 400,
                    color: 'rgba(255, 243, 225, 0.7)',
                    lineHeight: 1.4
                  }}
                >
                  {session.title || 'Untitled session'}
                </span>
                <span
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '13px',
                    fontWeight: 400,
                    color: 'rgba(255, 243, 225, 0.3)',
                    letterSpacing: '0.02em'
                  }}
                >
                  {formatDate(session.updated_at)}
                </span>
              </button>
            ))
          )}
        </div>
        
        {/* New conversation button */}
        <div 
          style={{
            padding: '16px 24px 32px 24px',
            borderTop: '1px solid rgba(255, 243, 225, 0.06)'
          }}
        >
          <button
            onClick={() => {
              onNewConversation();
              onClose();
            }}
            data-testid="mobile-new-conversation-btn"
            style={{
              width: '100%',
              padding: '14px 24px',
              background: 'rgba(196, 137, 74, 0.08)',
              border: '1px solid rgba(196, 137, 74, 0.2)',
              borderRadius: '12px',
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              letterSpacing: '0.08em',
              color: 'rgba(196, 137, 74, 0.7)',
              transition: 'all 0.2s ease'
            }}
          >
            start new session
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileHistorySheet;
